import { firstError, resolveLabels, unwrap, Widget, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { EventProtection } from "./EventProtection";
import { EVENT_PROTECTION_LABELS } from "./labels";
import { buildMatrixEvents } from "@nexus/widget-kit";
import type { CampaignScheduleData, EventProtectionConfig } from "./types";

export const LABELS = EVENT_PROTECTION_LABELS;

export const manifest: WidgetManifest = {
  id: "event-protection",
  name: "Event Protection",
  description: "Detection and response engines configured for one protected event.",
  version: "1.0.0",
  dataRequests: {
    schedule: {
      source: "campaign-schedule",
      contract: { name: "campaign-schedule", version: 1 },
      configurable: false,
    },
  },
  labels: LABELS,
};

export const defaultConfig: EventProtectionConfig = {
  title: "Event Protection",
};

export function Component({ data, config, dashboard }: WidgetProps<EventProtectionConfig>) {
  const labels = resolveLabels(LABELS, config);
  const error = firstError(data, ["schedule"]);
  const resolved = unwrap<CampaignScheduleData>(data.schedule);
  const events = buildMatrixEvents(resolved?.rows ?? []);
  const event =
    events.find((candidate) => candidate.protected_event_id === config.protectedEventId) ??
    events[0] ??
    null;

  if (error) {
    return (
      <Widget title={config.title ?? defaultConfig.title}>
        <WidgetIssueState error={error} />
      </Widget>
    );
  }

  if (!event && data.schedule?.isLoading) {
    return (
      <Widget title={config.title ?? defaultConfig.title}>
        <WidgetSkeleton shape="table" />
      </Widget>
    );
  }

  return (
    <Widget title={config.title ?? defaultConfig.title}>
      <EventProtection
        event={event}
        labels={labels}
        nowMs={config.nowMs ?? Date.now()}
        onOpenLiveEvents={
          dashboard.emitInteraction && event
            ? (tenantId, tenantLabel) =>
                dashboard.emitInteraction?.({
                  type: "campaign.tenant.live-events",
                  payload: {
                    event_id: event.event_id,
                    protected_event_id: event.protected_event_id,
                    tenant_id: tenantId,
                    tenant_label: tenantLabel,
                  },
                  source: { dataKey: "schedule" },
                })
            : undefined
        }
      />
    </Widget>
  );
}

const widgetModule: WidgetModule<EventProtectionConfig> = { manifest, Component, defaultConfig };

export default widgetModule;
